import { createApp } from 'vue'
import App from './App.vue'
import { router } from '@/services/router'
import { createVuetify } from 'vuetify'
import { aliases, mdi } from 'vuetify/iconsets/mdi'
import { Tell } from '@/services/Tell'
import { AuthService } from '@/contracts/AuthService'
import { EntraAuthService } from '@/services/EntraAuthService'
import { CommercialApi } from '@/services/CommercialApi'
import { EntraAuthConfig as entraAuthConfig } from '@/services/EntraAuthConfig'
import { CommercialApiConfig as commercialApiConfig } from '@/services/CommercialApiConfig'
import { SignalRMessageService } from '@/services/SignalRMessageService'
import telemetryService from '@/services/TelemetryService'
import { TelemetryContract } from '@/contracts/TelemetryContract';
import featureFlagService from '@/services/EnvFeatureFlagService'
import { LocalOverrideFeatureFlagService } from '@/services/LocalOverrideFeatureFlagService'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
// Order matters and is load-bearing:
//   1. brand tokens   — define the --moe-* custom properties
//   2. vuetify/styles — the framework's own rules
//   3. main.scss      — app overrides, which must come LAST to win
// main.scss used to be imported before vuetify/styles, which meant every app
// rule lost to the framework and had to reach for !important.
import '@moe-tech/design/css'
import 'vuetify/styles'
import './assets/styles/main.scss'
import "@mdi/font/css/materialdesignicons.css";
import { GoogleMap } from 'vue3-google-map';
import { useAppStore } from '@/stores/app.store';
import { AuthenticatedFetch, ResilientAuthenticatedFetch } from './services/AuthenticatedFetch'
import { TinyEmitterEventBus } from './services/TinyEmitterEventBus'
import { EventBus } from '@/contracts/EventBus'
import { PolicyService } from '@/contracts/PolicyService'
import { RateService } from '@/contracts/RateService'
import { ConfigService } from '@/contracts/ConfigService'
import { MessageService } from '@/contracts/MessageService'
import { buildThemes } from '@/services/theme'

export function startApp(authService: AuthService,
	policyService: PolicyService,
	rateService: RateService,
	configService: ConfigService,
	eventBus: EventBus
) {
	//const data = await mockService.getData();
	const app = createApp(App);

	const pinia = createPinia();

	pinia.use(piniaPluginPersistedstate);

	app.use(router);
	app.use(pinia);

	// Detect system preference for dark mode
	const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
	let defaultTheme = prefersDarkMode ? 'dark' : 'light';

	// Check the app store for current settings
	const appStore = useAppStore();

	// if the app store has settings; use those
	if (appStore.getTheme != null) {
		if (appStore.getTheme != 'system') {
			defaultTheme = appStore.getTheme;
		}
	}
	else {
		// save this to the store
		if (prefersDarkMode) {
			appStore.setTheme('system');
		}
		else {
			appStore.setTheme(defaultTheme);
		}
	}

	const vuetify = createVuetify({
		icons: {
			defaultSet: 'mdi',
			aliases,
			sets: {
				mdi,
			},
		},
		theme: buildThemes(defaultTheme),
	});

	app.use(vuetify);

	app.component('GoogleMap', GoogleMap);

	app.provide('auth', authService);
	app.provide('telemetry', telemetryService);
	app.provide('featureFlags', new LocalOverrideFeatureFlagService(featureFlagService));
	app.provide('tell', new Tell(eventBus));
	app.provide('policyService', policyService);
	app.provide('rateService', rateService);
	app.provide('configService', configService);

	app.provide('eventBus', eventBus);

	app.mount('#app');

}

if (process.env.VUE_APP_IS_TESTING !== 'true') {
	//start regularly
	const authService = new EntraAuthService(entraAuthConfig);
	const commercialApi = new CommercialApi(commercialApiConfig, new ResilientAuthenticatedFetch(new AuthenticatedFetch(authService)));
	const eventBus = new TinyEmitterEventBus();

	startApp(authService,
		commercialApi,
		commercialApi,
		commercialApi,
		eventBus
	);
}
// startApp(new EntraAuthService(entraAuthConfig));